import React, { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Block as BlockModel,
  BlockType,
  BlockWithNotes,
  emptyBlockContent,
} from '../../../../config/api/models/Block';
import { TrajectorySectionOrWorksheetType } from '../../../../config/api/models/Trajectory';
import { HoverBlock, IframeBlock, VideoBlock, WysiwygBlock } from './blocks';
import { GroupStudentSeriesScores } from '../../../../config/api/models/Score';
import { ExerciseDetailContent } from '../SeriesDetail/ExerciseList';
import { useUser } from '../../../shared';
import { StudyGuideBlock } from './blocks/StudyGuideBlock';
import { TeacherFiltersEnum } from '../../../shared/constants';
import { BlockNote } from 'src/config/api/models/BlockNote';
import theme from 'src/config/theme';
import { useStudentFeedbackForTrajectory } from '../../../../modules/feedback/hooks';
import {
  EditBlockButton,
  EditBlockCover,
  EmptyBlockWrapper,
} from '../TrajectoryDetail/blocks/atoms/Atoms';
import { useClickOutside } from './Hooks/useClickOutside';
import ColumnBlock from './blocks/ColumnBlock';
import { useAppDispatch, useAppSelector } from '../../../shared/hooks/useAppDispatch';
import { setHoverBlock, setNextBlock } from '../../../shared/slices/blocks/entity/BlocksSlice';
import { SpriteIconsLight } from 'src/config/api/models/FontAwesome';
import { ClusterWrapper, HoverScrollTarget, StyledBlockWrapper } from './Atoms';
import { useUpdateBlockRefs } from './Hooks/useUpdateBlockRef';
import { BlocksList, UpdatedBlockModel } from './BlocksList';
import { AddBlockButtonGroup } from './AddBlockButton';
import { makeStyles } from '@material-ui/core';
import { Indicators } from 'src/modules/shared/components/Indicators';
import { ClusterTitle } from './blocks/Cluster/Title/ClusterTitle';

export type BlockProps = BlockModel & { note: BlockNote | undefined };

const sectionStyles = makeStyles((theme) => ({
  section: {
    borderLeftWidth: '5px',
    borderLeftStyle: 'inset',
    borderRadius: 20,
    borderColor: 'rgba(51,153,0,0.15)',
    padding: 5,
  },
  emptySection: {
    borderWidth: '2px',
    borderStyle: 'dotted',
    borderRadius: 20,
    borderColor: 'rgba(51,153,0,0.15)',
    padding: 5,
  },
  simpleSection: {
    borderTopWidth: '2px',
    borderTopStyle: 'dotted',
    borderColor: 'rgba(51,153,0,0.40)',
    paddingTop: '1rem',
  },
}));

type Props = {
  pageId: string;
  pageType: TrajectorySectionOrWorksheetType;
  block: UpdatedBlockModel;
  onUpdate?: () => void;
  readOnly?: boolean;
  teacherProgressAndScores?: GroupStudentSeriesScores;
  bookId?: string;
  onSelectBlock?: Function | undefined;
  selectedBlock?: string | undefined;
  clusterId?: string;
  sectionId?: string;
  onSetFeedbackBlockId?: (id: string) => void;
  onToggleFeedback?: () => void;
};

const Block: FC<Props> = ({
  pageId,
  pageType,
  block,
  onUpdate,
  readOnly,
  teacherProgressAndScores,
  bookId,
  onSelectBlock = (blockId: string) => null,
  selectedBlock,
  clusterId,
  sectionId,
  onSetFeedbackBlockId,
  onToggleFeedback,
}) => {
  // hooks
  const { isTeacher, isStudent, isAuthor } = useUser();
  const { wrapperRef, handleClickOutside } = useClickOutside();
  const { feedback: studentFeedback } = useStudentFeedbackForTrajectory();
  const ref = useRef<Element>(null);
  const sectionClasses = sectionStyles();

  // redux
  const dispatch = useAppDispatch();

  const activeFilter = useAppSelector((state) => state.filters.activeFilter);
  const selectedGroup = useAppSelector((state) => state.filters.selectedGroup);
  const hoverBlock = useAppSelector((state) => state.blocks.hoverBlock);
  const nextBlock = useAppSelector((state) => state.blocks.nextBlock);
  const blockToDelete = useAppSelector((state) => state.blocks.blockToDelete);
  const teacherViewMode = useAppSelector((state) => state.teacherViewMode.teacherViewMode);
  const blocks = useAppSelector((state) => state.blocks.blocks);
  const showPresentationMode = useAppSelector(
    (state) => state.presentationMode.showPresentationMode
  );

  const feedbackEnabled = useMemo(
    () =>
      (isTeacher && activeFilter === TeacherFiltersEnum.FOLLOW_UP && selectedGroup !== '') ||
      (isStudent &&
        !!studentFeedback
          ?.find((item) => item.pageId === pageId)
          ?.feedback?.blockFeedbacks.some((item) => item.blockId === block.id)),
    [isTeacher, isStudent, activeFilter, selectedGroup, block.id, pageId, studentFeedback]
  );

  const handleMouseEnter = useCallback(() => {
    dispatch(setHoverBlock(block.id));
    dispatch(setNextBlock(block.id));
  }, [dispatch, block]);
  const handleMouseLeave = useCallback(() => {
    dispatch(setHoverBlock(undefined));
    dispatch(setNextBlock(undefined));
  }, [dispatch]);

  const { updateBlockRefs } = useUpdateBlockRefs();

  useEffect(() => {
    updateBlockRefs({ ref, id: block.parent_id ?? block.id });
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref]);

  let blockIsSelected = selectedBlock === block.id;

  useEffect(() => {
    if (readOnly && selectedBlock) {
      onSelectBlock(undefined);
      dispatch(setHoverBlock(undefined));
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readOnly, onSelectBlock]);

  useEffect(() => {
    const triggerAutosave = (event: MouseEvent) => {
      function closeEditMode() {
        onSelectBlock(nextBlock ?? undefined);
        dispatch(setHoverBlock(undefined));
      }
      const wiris = document.getElementById('wrs_modal_dialogContainer[0]');
      const wirisChildren: Node[] | null =
        wiris && Array.from(wiris?.childNodes).map((child) => child);
      return handleClickOutside(event, closeEditMode, [
        wrapperRef.current,
        wiris,
        wirisChildren?.map((child) => child),
      ]);
    };
    if (
      blockIsSelected &&
      !blockToDelete &&
      ![BlockType.H5P, BlockType.VIDEO, BlockType.IFRAME, BlockType.ALGEBRA_KIT].includes(
        block.type
      )
    ) {
      window.addEventListener('mousedown', triggerAutosave);
    }
    return () => {
      window.removeEventListener('mousedown', triggerAutosave);
    };
    //eslint-disable-next-line
  }, [wrapperRef, handleClickOutside, block.type, blockIsSelected]);

  const applyEmptyBlockWrapper = useCallback(
    (block: BlockModel) => {
      // in read only mode, empty blocks should not be seen at all
      if (readOnly) return false;
      // when block is selected we don't need visual aid to understand there's an
      // invisible block there
      if (blockIsSelected) return false;

      // Types of blocks that need emply wrapper

      // Wysiwyg
      if (block.type === 'wysiwyg' && (!block.content || block.content === emptyBlockContent))
        return true;
      // Column
      if (
        block.type === 'column' &&
        block.columns?.every(
          (blockItem) => !blockItem.content || blockItem.content === emptyBlockContent
        )
      )
        return true;
      return false;
    },
    [readOnly, blockIsSelected]
  );

  const getClusterIndex = (): number | string => {
    const index = blocks
      ?.filter((filterItem) => filterItem.type === 'cluster')
      .findIndex((item) => item.id === block.id);

    if (index || index === 0) return index + 1;
    return '';
  };

  return (
    <div id={`trajectory_block_${block.parent_id ?? block.id}`} ref={ref as any}>
      {!isAuthor && block.type !== 'cluster' && block.type !== 'section' && (
        <Indicators
          block={block}
          pageId={pageId}
          leftMargin={-20}
          onClickFeedback={() => {
            onSetFeedbackBlockId && onSetFeedbackBlockId(block.id);
            onToggleFeedback && onToggleFeedback();
          }}
        />
      )}

      <StyledBlockWrapper
        onMouseEnter={readOnly ? () => null : handleMouseEnter}
        onMouseLeave={readOnly ? () => null : handleMouseLeave}
        id={`block_wrapper-${block.id}`}
        condition={
          block.type !== 'cluster' && !clusterId && block.type !== 'section' && !sectionId
            ? 'true'
            : 'false'
        }
        ref={wrapperRef as any}
      >
        {applyEmptyBlockWrapper(block) && <EmptyBlockWrapper />}
        {hoverBlock === block.id && !readOnly && block.type !== BlockType.CLUSTER && (
          <HoverBlock
            pageId={pageId}
            block={block}
            type={pageType}
            clusterId={clusterId}
            sectionId={sectionId}
          />
        )}
        {hoverBlock === block.id &&
          !blockIsSelected &&
          [BlockType.WYSIWYG, BlockType.VIDEO, BlockType.STUDY_GUIDE].includes(block.type) && (
            <EditBlockCover>
              <EditBlockButton
                faIcon={SpriteIconsLight.PENCIL}
                large={true}
                dashed={true}
                border={undefined}
                bgColor={theme.palette.background.default}
                color={theme.palette.ternary.main}
                onClick={() => onSelectBlock(block.id)}
              />
            </EditBlockCover>
          )}
        {
          {
            column: (
              <ColumnBlock
                hoverActive={hoverBlock === block.id && !readOnly}
                block={block}
                key={block.id}
                pageId={pageId}
                bookId={bookId as string}
                readOnly={readOnly}
                onSelectBlock={onSelectBlock}
              />
            ),
            wysiwyg: (
              <WysiwygBlock
                hoverActive={hoverBlock === block.id && !readOnly}
                block={block}
                pageId={pageId}
                bookId={bookId!}
                readOnly={!blockIsSelected}
                feedbackEnabled={feedbackEnabled}
              />
            ),
            h5p: (
              <ExerciseDetailContent
                expanded={`exercise_${block.id}`}
                seriesId={pageId}
                pageType={pageType}
                exercise={block}
                mode={(isAuthor || teacherViewMode === 'edit') && !readOnly ? 'edit' : 'read'}
                teacherProgressAndScores={teacherProgressAndScores}
                hideDeleteButton={isAuthor || teacherViewMode === 'edit'}
                onUpdateExercise={onUpdate}
                hideTitlePrefix
                lazyLoad={!showPresentationMode}
                feedbackEnabled={feedbackEnabled}
              />
            ),
            book_widget: (
              <ExerciseDetailContent
                expanded={`exercise_${block.id}`}
                seriesId={pageId}
                pageType={pageType}
                exercise={block}
                mode={(isAuthor || teacherViewMode === 'edit') && !readOnly ? 'edit' : 'read'}
                teacherProgressAndScores={teacherProgressAndScores}
                hideDeleteButton={isAuthor || teacherViewMode === 'edit'}
                onUpdateExercise={onUpdate}
                hideTitlePrefix
                lazyLoad={!showPresentationMode}
                feedbackEnabled={feedbackEnabled}
              />
            ),
            algebra_kit: (
              <ExerciseDetailContent
                expanded={`exercise_${block.id}`}
                seriesId={pageId}
                pageType={pageType}
                exercise={block}
                mode={(isAuthor || teacherViewMode === 'edit') && !readOnly ? 'edit' : 'read'}
                teacherProgressAndScores={teacherProgressAndScores}
                hideDeleteButton={isAuthor || teacherViewMode === 'edit'}
                onUpdateExercise={onUpdate}
                hideTitlePrefix
                lazyLoad={!showPresentationMode}
                feedbackEnabled={feedbackEnabled}
              />
            ),
            iframe: (
              <IframeBlock
                bookId={bookId!}
                block={block}
                pageId={pageId}
                type={pageType}
                mode={isAuthor || teacherViewMode === 'edit' ? 'edit' : 'read'}
                feedbackEnabled={feedbackEnabled}
                onSelectBlock={onSelectBlock}
                selectedBlock={selectedBlock}
                readOnly={readOnly}
              />
            ),
            video: (
              <VideoBlock
                block={block}
                pageId={pageId}
                feedbackEnabled={feedbackEnabled}
                selectedBlock={selectedBlock}
                onSelectBlock={onSelectBlock}
              />
            ),
            study_guide: (
              <StudyGuideBlock
                block={block}
                pageId={pageId}
                bookId={bookId!}
                readOnly={!blockIsSelected}
                feedbackEnabled={feedbackEnabled}
              />
            ),
            cluster: (
              <ClusterWrapper elevation={0}>
                {!block?.hideClusterTitle && (
                  <ClusterTitle index={getClusterIndex()} cluster={block} />
                )}

                <div style={{ padding: '2rem' }}>
                  <BlocksList
                    pageId={pageId}
                    pageType={pageType}
                    readOnly={readOnly}
                    blocks={block.sections as BlockWithNotes[]}
                    clusterId={block.id}
                  />
                </div>
              </ClusterWrapper>
            ),
            section: (
              <div className={!readOnly ? sectionClasses.simpleSection : ''}>
                {!readOnly && pageType !== 'worksheet_exercises' && (
                  <AddBlockButtonGroup
                    previousBlockId={block.id}
                    clusterId={clusterId}
                    sectionId={block.id}
                    pageId={pageId}
                  />
                )}
                <BlocksList
                  pageId={pageId}
                  pageType={pageType}
                  readOnly={readOnly}
                  blocks={block.childBlocks as BlockWithNotes[]}
                  sectionId={block.id}
                  clusterId={clusterId}
                />
              </div>
            ),
            text_exercise: (
              <ExerciseDetailContent
                expanded={`exercise_${block.id}`}
                seriesId={pageId}
                pageType={pageType}
                exercise={block}
                mode={(isAuthor || teacherViewMode === 'edit') && !readOnly ? 'edit' : 'read'}
                teacherProgressAndScores={teacherProgressAndScores}
                hideDeleteButton={isAuthor || teacherViewMode === 'edit'}
                onUpdateExercise={onUpdate}
                hideTitlePrefix
                lazyLoad={!showPresentationMode}
                feedbackEnabled={feedbackEnabled}
              />
            ),
          }[block.type]
        }

        <HoverScrollTarget id={`block_hover_scroll_target_${block.id}`} />
      </StyledBlockWrapper>
    </div>
  );
};

export default Block;
